/**
 * The top bar's notifications (change spec 2026-09-24/topbar, design 2): the row shape of
 * `notifications_feed` and where each notice opens. Pure, so the mapping is tested without a screen.
 */

export const NOTIFICATION_KINDS = [
  'chat_message',
  'load_rejected',
  'task_assigned',
  'document_expiring',
  'invoice_overdue',
  'load_delivered',
  'pod_received',
  // audit-b-receivables (0306): a broker payment past due.
  'receivable_overdue',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface Notification {
  /** Stable per item: `chat:<thread>`, `task:<task>`, `event:<load event>`. */
  id: string;
  kind: NotificationKind;
  /** ISO timestamp: when it happened (a task: last touched, or the start of its due day). */
  at: string;
  unread: boolean;
  loadId: string | null;
  loadNumber: string | null;
  threadId: string | null;
  taskId: string | null;
  driverName: string | null;
  /** A task's stored title (English, written by SQL). */
  title: string | null;
  /** chat: the unread message count; a task: its stored detail (a rejection reason). */
  detail: string | null;
  dueAt: string | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

export function toNotification(r: Record<string, unknown>): Notification | null {
  const kind = r.kind;
  if (typeof kind !== 'string' || !(NOTIFICATION_KINDS as readonly string[]).includes(kind)) return null;
  return {
    id: String(r.id),
    kind: kind as NotificationKind,
    at: String(r.at),
    unread: r.unread === true,
    loadId: str(r.load_id),
    loadNumber: str(r.load_number),
    threadId: str(r.thread_id),
    taskId: str(r.task_id),
    driverName: str(r.driver_name),
    title: str(r.title),
    detail: str(r.detail),
    dueAt: str(r.due_at),
  };
}

const TASK_SOURCE: Partial<Record<NotificationKind, string>> = {
  load_rejected: 'load_rejected',
  document_expiring: 'document_expiry',
  invoice_overdue: 'invoice_overdue',
  receivable_overdue: 'receivable_overdue',
};

/** Where the office acts on it: the thread, the load, or the task list filtered to the source. */
export function notificationHref(n: Notification): string {
  if (n.kind === 'chat_message' && n.threadId) return `/chat/${n.threadId}`;
  if (n.loadId && (n.kind === 'load_delivered' || n.kind === 'pod_received' || n.kind === 'load_rejected')) {
    return `/loads/${n.loadId}`;
  }
  // The receivable itself: its sheet opens from any tab of Broker payments.
  if (n.kind === 'receivable_overdue' && n.loadId) return `/brokers/payments?load=${n.loadId}`;
  const source = TASK_SOURCE[n.kind];
  if (source) return `/tasks?source=${source}&view=all`;
  return '/tasks';
}
